/**
 * DOM Fader - ES6 Module Version
 * Fade DOM elements in and out with customizable options
 */

class DomFader {
  constructor() {
    this.defaultOptions = {
      duration: 400,
      easing: 'ease-in-out',
      display: 'block'
    };
  }

  /**
   * Fade in an element
   * @param {HTMLElement|string} element - Element or selector
   * @param {Object} options - Fade options
   * @returns {Promise}
   */
  fadeIn(element, options = {}) {
    const el = typeof element === 'string' ? document.querySelector(element) : element;
    if (!el) return Promise.reject(new Error('Element not found'));

    const opts = { ...this.defaultOptions, ...options };
    
    return new Promise((resolve) => {
      // Set initial state
      el.style.opacity = '0';
      el.style.display = opts.display;
      el.style.transition = `opacity ${opts.duration}ms ${opts.easing}`;
      
      // Force reflow
      el.offsetHeight;
      
      // Start fade in
      el.style.opacity = '1';
      
      // Resolve when animation completes
      setTimeout(() => {
        resolve(el);
      }, opts.duration);
    });
  }

  /**
   * Fade out an element
   * @param {HTMLElement|string} element - Element or selector
   * @param {Object} options - Fade options
   * @returns {Promise}
   */
  fadeOut(element, options = {}) {
    const el = typeof element === 'string' ? document.querySelector(element) : element;
    if (!el) return Promise.reject(new Error('Element not found'));

    const opts = { ...this.defaultOptions, ...options };
    
    return new Promise((resolve) => {
      // Set transition
      el.style.transition = `opacity ${opts.duration}ms ${opts.easing}`;
      
      // Start fade out
      el.style.opacity = '0';
      
      // Hide element and resolve when animation completes
      setTimeout(() => {
        el.style.display = 'none';
        resolve(el);
      }, opts.duration);
    });
  }

  /**
   * Toggle fade in/out based on current visibility
   * @param {HTMLElement|string} element - Element or selector
   * @param {Object} options - Fade options
   * @returns {Promise}
   */
  fadeToggle(element, options = {}) {
    const el = typeof element === 'string' ? document.querySelector(element) : element;
    if (!el) return Promise.reject(new Error('Element not found'));

    const isVisible = el.style.display !== 'none' && el.style.opacity !== '0';
    
    if (isVisible) {
      return this.fadeOut(el, options);
    } else {
      return this.fadeIn(el, options);
    }
  }

  /**
   * Fade multiple elements in sequence
   * @param {Array} elements - Array of elements or selectors
   * @param {Object} options - Fade options
   * @returns {Promise}
   */
  fadeInSequence(elements, options = {}) {
    const opts = { ...this.defaultOptions, ...options };
    const delay = opts.delay || 100;
    
    return elements.reduce((promise, element, index) => {
      return promise.then(() => {
        return new Promise((resolve) => {
          setTimeout(() => {
            this.fadeIn(element, opts).then(resolve);
          }, index * delay);
        });
      });
    }, Promise.resolve());
  }

  /**
   * Fade multiple elements out in sequence
   * @param {Array} elements - Array of elements or selectors
   * @param {Object} options - Fade options
   * @returns {Promise}
   */
  fadeOutSequence(elements, options = {}) {
    const opts = { ...this.defaultOptions, ...options };
    const delay = opts.delay || 100;
    
    return elements.reduce((promise, element, index) => {
      return promise.then(() => {
        return new Promise((resolve) => {
          setTimeout(() => {
            this.fadeOut(element, opts).then(resolve);
          }, index * delay);
        });
      });
    }, Promise.resolve());
  }

  /**
   * Cross-fade between two elements
   * @param {HTMLElement|string} elementOut - Element to fade out
   * @param {HTMLElement|string} elementIn - Element to fade in
   * @param {Object} options - Fade options
   * @returns {Promise}
   */
  crossFade(elementOut, elementIn, options = {}) {
    return Promise.all([
      this.fadeOut(elementOut, options),
      this.fadeIn(elementIn, options)
    ]);
  }
}

// Create instance
const domFader = new DomFader();

// Export both the class and a default instance
export { DomFader };
export default domFader;